import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { clientsLookup, invalidateLookup } from "@/lib/lookupCache";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import ViewToggle from "@/components/ViewToggle";
import useViewMode from "@/lib/useViewMode";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import SearchableSelect from "@/components/ui/SearchableSelect";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Gem, Plus, Search, Building2, Globe, Layers, Library, LayoutDashboard,
  ListPlus, GitCompare, Wand2, Activity, Printer, X, Check, HeartPulse,
  Sparkles, History, LineChart,
} from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { fmtRelative } from "@/lib/format";
import MyRoleChip from "@/components/MyRoleChip";
import BrandsDashboardTab from "@/components/Brand/BrandsDashboardTab";
import BrandLibrary from "@/pages/app/BrandLibrary";
import BrandBulkAddDialog from "@/components/Brand/BrandBulkAddDialog";
import BrandBulkEditDialog from "@/components/Brand/BrandBulkEditDialog";
import BrandCompareDialog from "@/components/Brand/BrandCompareDialog";
import BrandActivityDialog from "@/components/Brand/BrandActivityDialog";
import BrandPulse from "@/components/Brand/BrandPulse";
import PortfolioPulseTab from "@/components/Brand/PortfolioPulseTab";
import BrandPrintPreview from "@/components/Brand/BrandPrintPreview";
import CompareSnapshotsDialog from "@/components/Brand/CompareSnapshotsDialog";
import { useJobPoller } from "@/lib/useJobPoller";

const STATUS_TONES = { active: "border-emerald-500/40 text-emerald-700", paused: "border-amber-500/40 text-amber-700", archived: "border-border text-muted-foreground" };
const STATUS_TABLE_TONE = {
  active: "border-emerald-500/40 text-emerald-700 bg-emerald-500/5",
  paused: "border-amber-500/40 text-amber-700 bg-amber-500/5",
  archived: "border-border text-muted-foreground bg-secondary/40",
};

export default function Brands() {
  const { activeWorkspaceId } = useAuth();
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const tab = sp.get("tab") || "brands";
  const setTab = (t) => setSp((prev) => {
    const next = new URLSearchParams(prev);
    if (t === "brands") next.delete("tab"); else next.set("tab", t);
    return next;
  });
  const [rows, setRows] = useState(null);
  const [clients, setClients] = useState([]);
  const [industries, setIndustries] = useState({ industries: [], sub_industries: {} });
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("_all");
  const [clientF, setClientF] = useState("_all");
  const [creating, setCreating] = useState(false);
  const [bulkAdding, setBulkAdding] = useState(false);
  const [bulkEditing, setBulkEditing] = useState(false);
  const [comparingIds, setComparingIds] = useState(null);
  const [activityBrand, setActivityBrand] = useState(null);
  const [printBrandId, setPrintBrandId] = useState(null);
  const [snapshotsOpen, setSnapshotsOpen] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [briefJobId, setBriefJobId] = useState(null);
  const [mode, setMode] = useViewMode("brands");
  const { job: briefJob } = useJobPoller(activeWorkspaceId, briefJobId);

  const load = async () => {
    if (!activeWorkspaceId) return;
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (status !== "_all") p.set("status", status);
    if (clientF !== "_all") p.set("client_id", clientF);
    try {
      const r = await api.get(`/workspaces/${activeWorkspaceId}/brands/list-plus?${p.toString()}`);
      setRows(r.data || []);
      // Reconcile selection with current row set
      const rowIds = new Set((r.data || []).map((b) => b.id));
      setSelected((cur) => new Set([...cur].filter((id) => rowIds.has(id))));
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load brands")); setRows([]); }
  };
  useEffect(() => { load(); }, [activeWorkspaceId, status, clientF]);
  useEffect(() => {
    if (!briefJob) return;
    if (briefJob.status === "done" || briefJob.status === "partial") {
      toast.success(
        `AI briefs complete · ${briefJob.done} generated${briefJob.failed ? ` · ${briefJob.failed} failed` : ""}`,
        { duration: 6000 });
      load();
      setBriefJobId(null);
    } else if (briefJob.status === "failed") {
      toast.error("Bulk AI brief job failed");
      setBriefJobId(null);
    }
  }, [briefJob]);
  useEffect(() => {
    if (!activeWorkspaceId) return;
    clientsLookup(activeWorkspaceId).then((rows) => setClients(rows || []));
    api.get(`/reference/industries`).then((r) => setIndustries(r.data || { industries: [], sub_industries: {} })).catch(() => {});
  }, [activeWorkspaceId]);

  const toggleSelect = (id, forceOn) => setSelected((s) => {
    const nx = new Set(s);
    if (forceOn === true) nx.add(id);
    else if (forceOn === false) nx.delete(id);
    else (nx.has(id) ? nx.delete(id) : nx.add(id));
    return nx;
  });
  const selectAll = (on) => setSelected(on ? new Set((rows || []).map((b) => b.id)) : new Set());
  const selectedList = useMemo(() => [...selected], [selected]);

  // Inline-edit helper — PATCH single field on a brand.
  const patchBrand = async (bid, field, value) => {
    try {
      await api.patch(`/brands/${bid}`, { [field]: value });
      setRows((rs) => (rs || []).map((r) => r.id === bid ? { ...r, [field]: value } : r));
      toast.success("Saved");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Save failed"));
      load();
    }
  };

  const startCompare = () => {
    if (selected.size < 2) { toast.error("Select at least 2 brands to compare"); return; }
    if (selected.size > 6) { toast.error("Compare up to 6 brands at once"); return; }
    setComparingIds(selectedList);
  };

  const startBulkBrief = async (overwrite) => {
    if (selected.size === 0) { toast.error("Select brands first"); return; }
    try {
      const r = await api.post(
        `/workspaces/${activeWorkspaceId}/brands/bulk-ai-brief`,
        { ids: selectedList, overwrite: !!overwrite });
      setBriefJobId(r.data.job_id);
      toast.info(
        `Generating AI briefs for ${r.data.with_website} brand${r.data.with_website === 1 ? "" : "s"} in the background…`
        + (r.data.skipped_no_website ? ` (${r.data.skipped_no_website} skipped — no website)` : ""));
    } catch (e) {
      toast.error(apiErrorMessage(e, "Bulk AI brief failed"));
    }
  };

  const listCols = useMemo(() => ({}), []); // placeholder — columns rendered by BrandsListTable

  return (
    <>
      <PageHeader
        eyebrow="CRM"
        title="Brands"
        description="Brand management hub — strategy, SWOT, competitors, voice, guidelines and a shared library of logos, colors and fonts."
        testid="brands-header"
        actions={
          <div className="flex items-center gap-2">
            {tab === "brands" && <ViewToggle mode={mode} onChange={setMode} testid="brands-view-toggle" />}
            {tab === "brands" && (
              <Button variant="outline" className="rounded-none"
                        onClick={async () => {
                          const t = toast.loading("Capturing brand pulses…");
                          try {
                            const r = await api.post(`/workspaces/${activeWorkspaceId}/brands/pulse/snapshot`);
                            toast.success(`Captured pulse for ${r.data.captured} of ${r.data.total} brands`, { id: t });
                            load();
                          } catch (e) { toast.error(apiErrorMessage(e, "Capture failed"), { id: t }); }
                        }}
                        data-testid="brands-capture-pulse-btn"
                        title="Capture a pulse-score data point for every brand right now">
                <Sparkles className="w-4 h-4 mr-2" /> Capture pulse
              </Button>
            )}
            {tab === "brands" && (
              <Button variant="outline" className="rounded-none" onClick={() => setSnapshotsOpen(true)}
                        data-testid="brands-snapshots-btn"
                        title="Saved brand comparisons">
                <History className="w-4 h-4 mr-2" /> Snapshots
              </Button>
            )}
            {tab === "brands" && (
              <Button variant="outline" className="rounded-none" onClick={() => setBulkAdding(true)}
                        data-testid="brands-bulk-add-btn">
                <ListPlus className="w-4 h-4 mr-2" /> Bulk add
              </Button>
            )}
            {tab === "brands" && <Button className="rounded-none" onClick={() => setCreating(true)} data-testid="brands-new-btn"><Plus className="w-4 h-4 mr-2" /> New brand</Button>}
          </div>
        }
      />
      {/* Tabs */}
      <div className="px-4 sm:px-6 md:px-10 border-b border-border flex gap-0" data-testid="brands-tabs">
        <TabBtn active={tab === "dashboard"} onClick={() => setTab("dashboard")} testid="brands-tab-dashboard">
          <LayoutDashboard className="w-3.5 h-3.5" /> Dashboard
        </TabBtn>
        <TabBtn active={tab === "brands"} onClick={() => setTab("brands")} testid="brands-tab-brands">
          <Gem className="w-3.5 h-3.5" /> Brands
        </TabBtn>
        <TabBtn active={tab === "pulse"} onClick={() => setTab("pulse")} testid="brands-tab-pulse">
          <LineChart className="w-3.5 h-3.5" /> Portfolio Pulse
        </TabBtn>
        <TabBtn active={tab === "library"} onClick={() => setTab("library")} testid="brands-tab-library">
          <Library className="w-3.5 h-3.5" /> Brand library
        </TabBtn>
      </div>

      {tab === "dashboard" && (
        <div className="px-4 sm:px-6 md:px-10 pb-10 pt-6">
          <BrandsDashboardTab />
        </div>
      )}

      {tab === "pulse" && <PortfolioPulseTab />}

      {tab === "library" && <BrandLibrary embedded />}

      {tab === "brands" && (
      <div className="px-4 sm:px-6 md:px-10 pb-10 pt-6 space-y-4">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-foreground/50" />
            <Input className="pl-9 h-9 rounded-none" placeholder="Search brands…" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") load(); }} data-testid="brands-search" />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="h-9 w-36 rounded-none" data-testid="brands-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">All statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="paused">Paused</SelectItem>
              <SelectItem value="archived">Archived</SelectItem>
            </SelectContent>
          </Select>
          <SearchableSelect
            value={clientF}
            onValueChange={setClientF}
            options={[{ value: "_all", label: "All clients" },
                          ...clients.map((c) => ({ value: c.id, label: c.name }))]}
            placeholder="All clients"
            sort="asc"
            width={200}
            data-testid="brands-client-filter"
          />
        </div>

        {mode === "list" && selected.size > 0 && (
          <div className="flex items-center gap-2 flex-wrap border border-primary/40 bg-primary/5 px-3 py-2"
                 data-testid="brands-bulk-actionbar">
            <Check className="w-4 h-4 text-primary" />
            <span className="text-xs font-medium">
              {selected.size} brand{selected.size === 1 ? "" : "s"} selected
            </span>
            <Button variant="outline" size="sm" className="rounded-none h-7 text-xs ml-2"
                      onClick={() => setBulkEditing(true)}
                      data-testid="brands-bulk-edit-btn">
              <Wand2 className="w-3.5 h-3.5 mr-1" /> Bulk edit
            </Button>
            <Button variant="outline" size="sm" className="rounded-none h-7 text-xs"
                      onClick={startCompare} data-testid="brands-compare-btn">
              <GitCompare className="w-3.5 h-3.5 mr-1" /> Compare ({selected.size})
            </Button>
            <Button variant="outline" size="sm"
                      className="rounded-none h-7 text-xs border-violet-500/50 text-violet-700 hover:bg-violet-50"
                      onClick={() => startBulkBrief(false)}
                      disabled={!!briefJobId}
                      data-testid="brands-bulk-brief-btn">
              <Sparkles className="w-3.5 h-3.5 mr-1" />
              {briefJobId ? `AI briefs… (${briefJob?.done || 0}/${briefJob?.total || 0})`
                             : `AI brief · ${selected.size}`}
            </Button>
            <Button variant="ghost" size="sm" className="rounded-none h-7 text-xs ml-auto"
                      onClick={() => selectAll(false)} data-testid="brands-clear-selection">
              <X className="w-3.5 h-3.5 mr-1" /> Clear
            </Button>
          </div>
        )}

        {!rows ? <div className="text-sm text-muted-foreground">Loading…</div>
          : rows.length === 0 ? (
            <EmptyState title="No brands yet" description="Create your first brand to define its strategy, voice, guidelines, competitors and more." icon={Gem} testid="brands-empty"
              action={
                <div className="flex gap-2 justify-center">
                  <Button variant="outline" className="rounded-none" onClick={() => setBulkAdding(true)} data-testid="brands-empty-bulk-cta">
                    <ListPlus className="w-4 h-4 mr-2" /> Bulk add
                  </Button>
                  <Button className="rounded-none" onClick={() => setCreating(true)} data-testid="brands-empty-cta">New brand</Button>
                </div>
              } />
          ) : mode === "list" ? (
            <BrandsListTable
              rows={rows}
              clients={clients}
              industries={industries}
              selected={selected}
              onToggleSelect={toggleSelect}
              onSelectAll={selectAll}
              onOpen={(b) => navigate(`/app/brands/${b.id}`)}
              onActivity={(b) => setActivityBrand(b)}
              onPrint={(b) => setPrintBrandId(b.id)}
              onPatch={patchBrand}
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="brands-grid">
              {rows.map((b) => (
                <div key={b.id} className="border border-border p-4 hover:border-primary transition-colors relative group"
                       data-testid={`brand-card-${b.id}`}>
                  <Link to={`/app/brands/${b.id}`} className="block">
                    <div className="flex items-start gap-3">
                      {b.logo_url
                        ? <img src={b.logo_url} alt={b.name} className="w-10 h-10 object-contain border border-border bg-white" />
                        : <div className="w-10 h-10 border border-border bg-muted/40 flex items-center justify-center"><Gem className="w-4 h-4 text-muted-foreground" /></div>}
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold truncate flex items-center gap-2">
                          {b.name}
                          {b.my_role && <MyRoleChip role={b.my_role} perms={b.my_role_perms} entityKind="brand" />}
                        </div>
                        <div className="text-xs text-muted-foreground truncate">{b.industry || "—"}</div>
                      </div>
                      <Badge variant="outline" className={`rounded-none text-[10px] ${STATUS_TONES[b.status] || ""}`}>{b.status}</Badge>
                    </div>
                    <div className="mt-3 flex items-center gap-3 text-[11px] text-muted-foreground flex-wrap">
                      {b.client_name && <span className="inline-flex items-center gap-1"><Building2 className="w-3 h-3" />{b.client_name}</span>}
                      {b.website && <span className="inline-flex items-center gap-1 truncate max-w-[140px]"><Globe className="w-3 h-3" />{b.website.replace(/^https?:\/\//, "")}</span>}
                      <span className="inline-flex items-center gap-1"><Layers className="w-3 h-3" />{b.pillars_count} pillars · {b.competitors_count} competitors</span>
                    </div>
                    <div className="mt-2">
                      <HealthChip score={b.health_score} band={b.health_band} />
                    </div>
                  </Link>
                  <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 flex gap-1 transition-opacity">
                    <button type="button" onClick={(e) => { e.stopPropagation(); setActivityBrand(b); }}
                              className="p-1 border border-border bg-background hover:border-primary hover:text-primary"
                              title="Activity" data-testid={`brand-card-activity-${b.id}`}>
                      <Activity className="w-3 h-3" />
                    </button>
                    <button type="button" onClick={(e) => { e.stopPropagation(); setPrintBrandId(b.id); }}
                              className="p-1 border border-border bg-background hover:border-primary hover:text-primary"
                              title="Print / PDF" data-testid={`brand-card-print-${b.id}`}>
                      <Printer className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
      </div>
      )}
      {creating && <NewBrandDialog wsId={activeWorkspaceId} clients={clients} onClose={() => setCreating(false)} onCreated={load} />}
      {bulkAdding && <BrandBulkAddDialog wsId={activeWorkspaceId} clients={clients} onClose={() => setBulkAdding(false)} onCreated={load} />}
      {bulkEditing && (
        <BrandBulkEditDialog wsId={activeWorkspaceId} clients={clients}
                                 ids={selectedList}
                                 onClose={() => setBulkEditing(false)}
                                 onSaved={() => { setBulkEditing(false); load(); }} />
      )}
      {comparingIds && (
        <BrandCompareDialog wsId={activeWorkspaceId} ids={comparingIds}
                                onClose={() => setComparingIds(null)} />
      )}
      {activityBrand && (
        <BrandActivityDialog brand={activityBrand}
                                 onClose={() => setActivityBrand(null)} />
      )}
      {printBrandId && (
        <BrandPrintPreview brandId={printBrandId}
                               onClose={() => setPrintBrandId(null)} />
      )}
      {snapshotsOpen && (
        <CompareSnapshotsDialog onClose={() => setSnapshotsOpen(false)} />
      )}
    </>
  );
}

// ═══════════════════════ Inline-edit list table ═══════════════════════

const HEALTH_TONE = {
  excellent: "bg-emerald-500 text-white",
  good: "bg-sky-500 text-white",
  needs_work: "bg-amber-500 text-white",
  critical: "bg-rose-500 text-white",
};

function HealthChip({ score, band }) {
  if (score === undefined || score === null) return null;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 font-mono font-semibold ${HEALTH_TONE[band] || "bg-muted"}`}
            data-testid="brand-health-chip">
      <HeartPulse className="w-3 h-3" /> {score}
    </span>
  );
}

function InlineText({ value, onSave, placeholder = "—", testid }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");
  useEffect(() => setDraft(value || ""), [value]);
  const commit = () => {
    setEditing(false);
    if ((draft || "") !== (value || "")) onSave(draft);
  };
  if (editing) {
    return (
      <input value={draft} autoFocus
               onChange={(e) => setDraft(e.target.value)}
               onBlur={commit}
               onKeyDown={(e) => {
                 if (e.key === "Enter") commit();
                 if (e.key === "Escape") { setDraft(value || ""); setEditing(false); }
               }}
               className="h-7 w-full border border-primary bg-background px-1.5 text-xs rounded-none"
               data-testid={testid} />
    );
  }
  return (
    <button type="button" onClick={(e) => { e.stopPropagation(); setEditing(true); }}
              className="text-xs text-left w-full hover:bg-muted/40 px-1.5 py-1 border border-transparent hover:border-border"
              data-testid={testid}>
      {value || <span className="text-muted-foreground italic">{placeholder}</span>}
    </button>
  );
}

function InlineSelect({ value, options, onSave, testid, placeholder = "—", disabled = false }) {
  const [editing, setEditing] = useState(false);
  const cur = options.find((o) => o.value === value);
  if (editing) {
    return (
      <div className="min-w-[140px]" onBlur={() => setEditing(false)}>
        <SearchableSelect
          value={value || ""}
          onValueChange={(v) => { onSave(v || null); setEditing(false); }}
          options={options}
          placeholder={placeholder}
          clearable
          disabled={disabled}
          data-testid={testid}
          width="100%"
          triggerClassName="h-7 px-1.5 py-1 text-xs rounded-none border-primary"
        />
      </div>
    );
  }
  return (
    <button type="button" onClick={(e) => { e.stopPropagation(); if (!disabled) setEditing(true); }}
              disabled={disabled}
              className={`text-xs text-left w-full hover:bg-muted/40 px-1.5 py-1 border border-transparent hover:border-border ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
              data-testid={testid}>
      {cur ? cur.label : <span className="text-muted-foreground italic">{placeholder}</span>}
    </button>
  );
}

function TH({ sortKey, sortDir, k, align, w, onSort, children }) {
  return (
    <th style={{ width: w }} className={`px-2 py-2 text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border cursor-pointer whitespace-nowrap ${align === "right" ? "text-right" : "text-left"}`}
        onClick={() => onSort(k)}>
      <span className="inline-flex items-center gap-1">
        {children}
        {sortKey === k && <span className="text-primary">{sortDir === "asc" ? "↑" : "↓"}</span>}
      </span>
    </th>
  );
}

function BrandsListTable({ rows, clients, industries, selected, onToggleSelect,
                              onSelectAll, onOpen, onActivity, onPrint, onPatch }) {
  const [sort, setSort] = useState({ key: "name", dir: "asc" });
  const sorted = useMemo(() => {
    const sortedRows = [...rows];
    sortedRows.sort((a, b) => {
      const av = a[sort.key];
      const bv = b[sort.key];
      if (av === bv) return 0;
      if (av === null || av === undefined) return 1;
      if (bv === null || bv === undefined) return -1;
      const cmp = typeof av === "number" && typeof bv === "number"
        ? av - bv : String(av).localeCompare(String(bv));
      return sort.dir === "asc" ? cmp : -cmp;
    });
    return sortedRows;
  }, [rows, sort]);
  const setSortKey = (k) => setSort((s) => s.key === k
    ? { key: k, dir: s.dir === "asc" ? "desc" : "asc" }
    : { key: k, dir: "asc" });

  const allChecked = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const anyChecked = rows.some((r) => selected.has(r.id));

  const clientOptions = (clients || [])
    .map((c) => ({ value: c.id, label: c.name }))
    .sort((a, b) => (a.label || "").localeCompare(b.label || ""));
  const industryOptions = ((industries.industries || []).slice()
    .sort((a, b) => (a || "").localeCompare(b || ""))
    .map((i) => ({ value: i, label: i })));
  const STATUS_OPTIONS = [
    { value: "active", label: "Active" },
    { value: "paused", label: "Paused" },
    { value: "archived", label: "Archived" },
  ];
  const STATUS_TONE = {
    active: "border-emerald-500/40 text-emerald-700 bg-emerald-500/5",
    paused: "border-amber-500/40 text-amber-700 bg-amber-500/5",
    archived: "border-border text-muted-foreground bg-secondary/40",
  };

  const sortProps = { sortKey: sort.key, sortDir: sort.dir, onSort: setSortKey };

  return (
    <div className="border border-border bg-card overflow-x-auto" data-testid="brands-list-table">
      <table className="min-w-full text-sm">
        <thead className="bg-muted/30">
          <tr>
            <th className="px-2 py-2 w-8 sticky left-0 bg-muted/30 border-b border-border">
              <Checkbox checked={allChecked ? true : (anyChecked ? "indeterminate" : false)}
                          onCheckedChange={(v) => onSelectAll(!!v && v !== "indeterminate")}
                          data-testid="brands-select-all" />
            </th>
            <TH {...sortProps} k="name" w={220}>Brand</TH>
            <TH {...sortProps} k="health_score" align="right" w={70}>Health</TH>
            <TH {...sortProps} k="pulse_score" w={130}>Pulse</TH>
            <TH {...sortProps} k="status" w={100}>Status</TH>
            <TH {...sortProps} k="client_name" w={160}>Client</TH>
            <TH {...sortProps} k="industry" w={130}>Industry</TH>
            <TH {...sortProps} k="sub_industry" w={140}>Sub-industry</TH>
            <TH {...sortProps} k="website" w={200}>Website</TH>
            <TH {...sortProps} k="active_projects_count" align="right" w={80}>Projects</TH>
            <TH {...sortProps} k="active_packages_count" align="right" w={80}>Packages</TH>
            <TH {...sortProps} k="contacts_count" align="right" w={70}>Contacts</TH>
            <TH {...sortProps} k="pillars_count" align="right" w={70}>Pillars</TH>
            <TH {...sortProps} k="competitors_count" align="right" w={90}>Competitors</TH>
            <TH {...sortProps} k="socials_count" align="right" w={70}>Socials</TH>
            <TH {...sortProps} k="usps_count" align="right" w={70}>USPs</TH>
            <TH {...sortProps} k="personas_count" align="right" w={80}>Personas</TH>
            <TH {...sortProps} k="swot_count" align="right" w={70}>SWOT</TH>
            <TH {...sortProps} k="last_activity_at" align="right" w={110}>Last activity</TH>
            <th className="px-2 py-2 w-24 border-b border-border" />
          </tr>
        </thead>
        <tbody>
          {sorted.map((b) => (
            <tr key={b.id} className={`border-b border-border hover:bg-muted/20 ${selected.has(b.id) ? "bg-primary/5" : ""}`}
                data-testid={`brand-row-${b.id}`}>
              <td className="px-2 py-1.5 sticky left-0 bg-inherit">
                <Checkbox checked={selected.has(b.id)}
                            onCheckedChange={(v) => onToggleSelect(b.id, !!v)}
                            data-testid={`brand-row-select-${b.id}`} />
              </td>
              <td className="px-2 py-1.5">
                <div className="flex items-center gap-2 min-w-0 cursor-pointer" onClick={() => onOpen(b)}>
                  {b.logo_url
                    ? <img src={b.logo_url} alt="" className="w-6 h-6 object-contain border border-border bg-white shrink-0" />
                    : <div className="w-6 h-6 border border-border bg-muted/40 flex items-center justify-center shrink-0"><Gem className="w-3 h-3 text-muted-foreground" /></div>}
                  <span className="font-medium truncate hover:text-primary">{b.name}</span>
                </div>
              </td>
              <td className="px-2 py-1.5 text-right"><HealthChip score={b.health_score} band={b.health_band} /></td>
              <td className="px-2 py-1.5">
                <BrandPulse score={b.pulse_score} trend={b.pulse_trend}
                                delta={(b.pulse_trend?.length >= 2)
                                          ? b.pulse_trend[b.pulse_trend.length - 1] - b.pulse_trend[b.pulse_trend.length - 2]
                                          : 0} />
              </td>
              <td className="px-1 py-1">
                <InlineSelect value={b.status} options={STATUS_OPTIONS}
                                  onSave={(v) => onPatch(b.id, "status", v)}
                                  testid={`brand-row-status-${b.id}`} />
                <div className="hidden">
                  <span className={`text-[10px] uppercase tracking-wider border px-1.5 py-0.5 ${STATUS_TONE[b.status] || ""}`}>{b.status}</span>
                </div>
              </td>
              <td className="px-1 py-1">
                <InlineSelect value={b.client_id} options={clientOptions}
                                  onSave={(v) => onPatch(b.id, "client_id", v)}
                                  testid={`brand-row-client-${b.id}`} />
              </td>
              <td className="px-1 py-1">
                <InlineSelect value={b.industry} options={industryOptions}
                                  onSave={(v) => onPatch(b.id, "industry", v)}
                                  testid={`brand-row-industry-${b.id}`} />
              </td>
              <td className="px-1 py-1">
                <InlineSelect
                  value={b.sub_industry}
                  options={((industries.sub_industries || {})[b.industry] || [])
                    .slice()
                    .sort((a, x) => (a || "").localeCompare(x || ""))
                    .map((s) => ({ value: s, label: s }))}
                  onSave={(v) => onPatch(b.id, "sub_industry", v)}
                  disabled={!b.industry}
                  placeholder={b.industry ? "—" : "Pick industry first"}
                  testid={`brand-row-sub-industry-${b.id}`}
                />
              </td>
              <td className="px-1 py-1">
                <InlineText value={b.website} placeholder="Add website"
                              onSave={(v) => onPatch(b.id, "website", v || null)}
                              testid={`brand-row-website-${b.id}`} />
              </td>
              <td className="px-2 py-1.5 text-right text-xs font-mono">{b.active_projects_count}<span className="text-muted-foreground">/{b.total_projects_count}</span></td>
              <td className="px-2 py-1.5 text-right text-xs font-mono">{b.active_packages_count}<span className="text-muted-foreground">/{b.total_packages_count}</span></td>
              <td className="px-2 py-1.5 text-right text-xs font-mono">{b.contacts_count}</td>
              <td className="px-2 py-1.5 text-right text-xs font-mono">{b.pillars_count}</td>
              <td className="px-2 py-1.5 text-right text-xs font-mono">{b.competitors_count}</td>
              <td className="px-2 py-1.5 text-right text-xs font-mono">{b.socials_count}</td>
              <td className="px-2 py-1.5 text-right text-xs font-mono">{b.usps_count}</td>
              <td className="px-2 py-1.5 text-right text-xs font-mono">{b.personas_count}</td>
              <td className="px-2 py-1.5 text-right text-xs font-mono">{b.swot_count}</td>
              <td className="px-2 py-1.5 text-right text-xs text-muted-foreground">{fmtRelative(b.last_activity_at)}</td>
              <td className="px-2 py-1.5">
                <div className="flex items-center gap-1 justify-end">
                  <button type="button" onClick={(e) => { e.stopPropagation(); onActivity(b); }}
                              className="p-1 border border-border hover:border-primary hover:text-primary"
                              title="Activity log" data-testid={`brand-row-activity-${b.id}`}>
                    <Activity className="w-3 h-3" />
                  </button>
                  <button type="button" onClick={(e) => { e.stopPropagation(); onPrint(b); }}
                              className="p-1 border border-border hover:border-primary hover:text-primary"
                              title="Print / PDF" data-testid={`brand-row-print-${b.id}`}>
                    <Printer className="w-3 h-3" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-4 py-2.5 text-sm font-semibold inline-flex items-center gap-1.5 border-b-2 -mb-px transition ${active ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}
      data-testid={testid}
    >
      {children}
    </button>
  );
}

export function NewBrandDialog({ wsId, clients, defaultClientId, onClose, onCreated }) {
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", client_id: defaultClientId || "", industry: "", sub_industry: "", website: "" });
  const [busy, setBusy] = useState(false);
  const [industriesCatalog, setIndustriesCatalog] = useState({ industries: [], sub_industries: {} });
  useEffect(() => {
    api.get(`/reference/industries`).then(({ data }) => setIndustriesCatalog(data)).catch(() => {});
  }, []);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const save = async () => {
    if (!form.name.trim()) { toast.error("Brand name required"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/workspaces/${wsId}/brands`, { ...form, client_id: form.client_id || null });
      invalidateLookup("brands", wsId);
      toast.success("Brand created");
      onCreated?.();
      onClose();
      nav(`/app/brands/${r.data.id}`);
    } catch (e) { toast.error(apiErrorMessage(e, "Create failed")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="new-brand-dialog">
        <DialogHeader><DialogTitle>New brand</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="label-eyebrow mb-1 inline-block">Brand name *</Label>
            <Input value={form.name} onChange={(e) => set("name", e.target.value)} className="h-10 rounded-none" data-testid="brand-name-input" />
          </div>
          {!defaultClientId && (
            <div>
              <Label className="label-eyebrow mb-1 inline-block">Client (CRM)</Label>
              <Select value={form.client_id || "_none"} onValueChange={(v) => set("client_id", v === "_none" ? "" : v)}>
                <SelectTrigger className="h-10 rounded-none" data-testid="brand-client-select"><SelectValue placeholder="No client" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">No client</SelectItem>
                  {(clients || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-eyebrow mb-1 inline-block">Industry</Label>
              <SearchableSelect
                value={form.industry}
                onValueChange={(v) => setForm((s) => ({ ...s, industry: v, sub_industry: "" }))}
                options={industriesCatalog.industries || []}
                placeholder="— Select industry —"
                clearable
                width="100%"
                data-testid="brand-industry-select"
              />
            </div>
            <div>
              <Label className="label-eyebrow mb-1 inline-block">Sub-industry</Label>
              <SearchableSelect
                value={form.sub_industry}
                onValueChange={(v) => set("sub_industry", v)}
                options={industriesCatalog.sub_industries?.[form.industry] || []}
                placeholder={form.industry ? "— Select sub-industry —" : "Pick industry first"}
                disabled={!form.industry}
                clearable
                width="100%"
                data-testid="brand-sub-industry-select"
              />
            </div>
          </div>
          <div>
            <Label className="label-eyebrow mb-1 inline-block">Website</Label>
            <Input value={form.website} onChange={(e) => set("website", e.target.value)} placeholder="https://…" className="h-10 rounded-none" data-testid="brand-website-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="rounded-none" data-testid="brand-create-save">{busy ? "Creating…" : "Create brand"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
